import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { toast } from "sonner";
import { Mic, Check, ExternalLink, AlertTriangle, RotateCcw, MessageSquareText, Settings2, ArrowLeft, PhoneCall } from "lucide-react";
import LiveDemoPanel from "./LiveDemoPanel";
import VoiceCallPanel from "./VoiceCallPanel";

export default function VoiceDemoAdmin() {
  const [data, setData] = useCachedState("webdemo", null);
  const [selected, setSelected] = useState("");
  const [about, setAbout] = useState("");
  const [greeting, setGreeting] = useState("");
  const [language, setLanguage] = useState("English");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("simulator");
  const [smsCommunity, setSmsCommunity] = useState("");
  const [mode, setMode] = useState("sms"); // sms | voice

  const load = useCallback(async () => {
    const r = await api.get("/admin/webdemo");
    setData(r.data);
    setSelected(r.data.tenant_id || "");
    setSmsCommunity((prev) => prev || r.data.tenant_id || r.data.communities?.[0]?.id || "");
    setAbout(r.data.about || "");
    setGreeting(r.data.greeting || "");
    setLanguage(r.data.default_language || "English");
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/webdemo", { tenant_id: selected, about, greeting, default_language: language });
      toast.success(`Demo saved — powered by ${r.data.community}`);
      load();
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;

  const siteUrl = window.location.origin;
  const dirty = selected !== data.tenant_id || about !== data.about || greeting !== data.greeting || language !== data.default_language;

  return (
    <div className={`space-y-6 ${tab === "simulator" ? "max-w-6xl" : "max-w-3xl"}`} data-testid="voice-demo-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <PageTitle title={<><Mic className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Voice &amp; SMS Simulator</>} about="Run a live demo against any community's brain — text it like SMS, or start a real voice call and talk to it." />
        </div>
        {tab === "simulator" && (
          <button data-testid="demo-tab-setup" onClick={() => setTab("setup")}
            className="shrink-0 inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white text-ink-600 font-semibold text-sm px-4 py-2 hover:border-ink hover:text-ink transition-colors">
            <Settings2 className="h-4 w-4" /> Website demo settings
          </button>
        )}
      </div>

      {tab === "simulator" && (
        <div className="space-y-5 max-w-none">
          <div className="bg-white rounded-2xl border border-sand-200 p-5">
            <label className="text-sm font-semibold">Which community's brain?</label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">The simulator answers live from this community's own documents &amp; roster.</p>
            <select data-testid="sms-sim-community-select" value={smsCommunity} onChange={(e) => setSmsCommunity(e.target.value)}
              className="w-full max-w-sm rounded-lg border border-sand-200 px-3 py-2.5 text-sm bg-white">
              {(data.communities || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="mt-4 inline-flex rounded-full bg-sand-100 border border-sand-200 p-1" data-testid="sim-mode-toggle">
              <button data-testid="sim-mode-sms" onClick={() => setMode("sms")}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${mode === "sms" ? "bg-ink text-white shadow-sm" : "text-ink-600 hover:text-ink"}`}>
                <MessageSquareText className="h-4 w-4" /> Text (SMS)
              </button>
              <button data-testid="sim-mode-voice" onClick={() => setMode("voice")}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${mode === "voice" ? "bg-ink text-white shadow-sm" : "text-ink-600 hover:text-ink"}`}>
                <PhoneCall className="h-4 w-4" /> Live voice call
              </button>
            </div>
          </div>
          {smsCommunity && (mode === "sms"
            ? <LiveDemoPanel key={`sms-${smsCommunity}`} tenantId={smsCommunity} />
            : <VoiceCallPanel key={`voice-${smsCommunity}`} tenantId={smsCommunity} />)}
        </div>
      )}

      {tab === "setup" && (
      <div className="space-y-6">
      <button data-testid="demo-back-to-sim" onClick={() => setTab("simulator")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-ink transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to simulator
      </button>
      <div>
        <h2 className="font-heading text-xl font-extrabold tracking-tight">Website demo setup</h2>
        <p className="text-muted-foreground text-sm mt-1">Controls the live voice + chat demos on your marketing site — which community powers them, the default language, greeting, and the product pitch.</p>
      </div>
      {!data.voice_enabled && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3 text-sm text-amber-900">
          <AlertTriangle className="h-5 w-5 shrink-0" /> Voice engine is not configured (missing OpenAI key). The chat demo still works; the voice demo won't connect until that's set.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-3">
        <label className="text-sm font-semibold">Demo community (powers resident answers)</label>
        <select data-testid="webdemo-community-select" value={selected} onChange={(e) => setSelected(e.target.value)}
          className="w-full rounded-lg border border-sand-200 px-3 py-2.5 text-sm bg-white">
          {data.communities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <p className="text-xs text-muted-foreground">Resident-style questions are answered from this community's uploaded documents. Manage those on the community's Documents page.</p>
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-3">
        <label className="text-sm font-semibold">Default language</label>
        <select data-testid="webdemo-language-select" value={language} onChange={(e) => setLanguage(e.target.value)}
          className="w-full rounded-lg border border-sand-200 px-3 py-2.5 text-sm bg-white">
          {(data.language_options || ["English"]).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <p className="text-xs text-muted-foreground">The demo bot always greets and replies in this language first. If a visitor writes in another language, it automatically mirrors them and replies in that language.</p>
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold">Greeting (first thing the assistant says)</label>
        </div>
        <textarea data-testid="webdemo-greeting" value={greeting} onChange={(e) => setGreeting(e.target.value)} rows={2}
          className="w-full rounded-lg border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold">About Ask Redline (product knowledge)</label>
          <button type="button" onClick={() => setAbout(data.about_default)} data-testid="webdemo-about-reset"
            className="inline-flex items-center gap-1 text-xs font-semibold text-ink-600 hover:text-redline-600">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default
          </button>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">This is what the demo tells prospects about Ask Redline — features, pricing, integrations, onboarding, compliance. Editable here only; it's kept separate from the community's documents and your marketing site.</p>
        <textarea data-testid="webdemo-about" value={about} onChange={(e) => setAbout(e.target.value)} rows={14}
          className="w-full rounded-lg border border-sand-200 px-3 py-2.5 text-[13px] leading-relaxed font-mono outline-none focus:ring-2 focus:ring-redline-500/30" />
      </div>

      <div className="flex items-center gap-3 sticky bottom-4">
        <button onClick={save} disabled={saving || !dirty} data-testid="webdemo-save"
          className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 disabled:opacity-50 flex items-center gap-2 shadow-lg">
          <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
        <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-redline-600 hover:underline">
          Open the demo <ExternalLink className="h-4 w-4" />
        </a>
      </div>
      </div>
      )}
    </div>
  );
}
